package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.ExpandLess
import androidx.compose.material.icons.outlined.ExpandMore
import androidx.compose.material.icons.outlined.Menu
import androidx.compose.material.icons.outlined.Notifications
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.i18n.strings
import org.gymly.model.AdminNavGroup
import org.gymly.model.AdminNotificationItem
import org.gymly.model.AdminRoute
import org.gymly.model.AdminSearchHit
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.ui.components.VerticalScrollColumn
import org.gymly.util.imagePreviewModel
import kotlin.time.Clock

@Composable
fun AdminSidebar(
    current: AdminRoute,
    onSelect: (AdminRoute) -> Unit,
    showReports: Boolean = false,
    showSettings: Boolean = true,
    showTermsMonitoring: Boolean = false,
    clientAccountsEnabled: Boolean = false,
    pendingApprovalsCount: Long = 0L,
    canOpen: (AdminRoute) -> Boolean = { true },
    modifier: Modifier = Modifier,
) {
    val t = strings()
    val expanded = remember {
        mutableStateMapOf<AdminNavGroup, Boolean>().apply {
            AdminNavGroup.entries.forEach { put(it, false) }
        }
    }
    // Mantener abierta la sección de la ruta activa.
    LaunchedEffect(current) {
        current.group?.let { expanded[it] = true }
    }

    Column(
        modifier
            .width(240.dp)
            .fillMaxHeight()
            .background(AdminColors.Sidebar),
    ) {
            Column(
                Modifier
                    .weight(1f)
                    .fillMaxWidth(),
            ) {
                VerticalScrollColumn(
                    Modifier
                        .fillMaxSize()
                        .widthIn(max = AdminLayoutBreakpoints.CONTENT_MAX_WIDTH.dp)
                        .align(Alignment.CenterHorizontally)
                        .padding(vertical = GymlySpace.lg),
                ) {
            // Branding público (logo sin JWT). Se refresca al cambiar de ruta
            // para que un logo subido en Configuración aparezca al volver.
            var brandName by remember { mutableStateOf("Gymly") }
            var logoUrl by remember { mutableStateOf<String?>(null) }
            LaunchedEffect(current) {
                val branding = runCatching { AdminRepo.api.publicCompany() }.getOrNull()
                brandName = branding?.name?.takeIf { it.isNotBlank() } ?: "Gymly"
                org.gymly.theme.BrandTheme.apply(branding?.brandColor)
                    logoUrl = branding
                    ?.takeIf { it.hasLogo }
                    ?.logoUrl
                    ?.takeIf { it.isNotBlank() }
                    ?.let { url ->
                        val sep = if (url.contains('?')) '&' else '?'
                        "$url${sep}v=${Clock.System.now().toEpochMilliseconds()}"
                    }
            }
            Row(
                Modifier.padding(horizontal = GymlySpace.lg, vertical = GymlySpace.sm),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                org.gymly.ui.components.BrandLogo(
                    logoUrl = logoUrl,
                    name = brandName,
                    height = 36.dp,
                ) {
                    Box(
                        Modifier
                            .size(36.dp)
                            .clip(CircleShape)
                            .background(AdminColors.OnBrand),
                        contentAlignment = Alignment.Center,
                    ) {
                        Text(
                            brandName.take(1).uppercase(),
                            color = AdminColors.Primary,
                            fontWeight = FontWeight.Bold,
                        )
                    }
                }
                Text(
                    brandName,
                    color = AdminColors.SidebarActive,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    modifier = Modifier.padding(start = 10.dp),
                )
            }
            Spacer(Modifier.height(GymlySpace.md))
            SidebarLink(AdminRoute.Dashboard, current, onSelect)
            if (canOpen(AdminRoute.BulkSignup)) {
                SidebarLink(AdminRoute.BulkSignup, current, onSelect)
            }
            if (showReports && canOpen(AdminRoute.Reports)) {
                SidebarLink(AdminRoute.Reports, current, onSelect)
            }
            AdminNavGroup.entries.forEach { group ->
                val routes = AdminRoute.entries.filter { route ->
                    route.group == group &&
                        (route != AdminRoute.Settings || showSettings) &&
                        (route != AdminRoute.TermsMonitoring || showTermsMonitoring) &&
                        (route != AdminRoute.ClientAccounts || clientAccountsEnabled) &&
                        (route == AdminRoute.Profile || canOpen(route))
                }
                if (routes.isEmpty()) return@forEach
                val isOpen = expanded[group] == true
                SidebarGroupHeader(
                    label = t.nav.group(group),
                    expanded = isOpen,
                    onToggle = { expanded[group] = !isOpen },
                )
                if (isOpen) {
                    routes.forEach { route ->
                        SidebarLink(
                            route = route,
                            current = current,
                            onSelect = onSelect,
                            badgeCount = if (route == AdminRoute.Settings) pendingApprovalsCount else 0L,
                        )
                    }
                }
            }
            }
        }
    }
}

@Composable
private fun SidebarGroupHeader(
    label: String,
    expanded: Boolean,
    onToggle: () -> Unit,
) {
    Row(
        Modifier
            .fillMaxWidth()
            .clickable(onClick = onToggle)
            .padding(horizontal = GymlySpace.lg, vertical = GymlySpace.sm),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(
            label.uppercase(),
            color = AdminColors.SidebarText.copy(alpha = 0.65f),
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier.weight(1f),
        )
        val t = strings()
        Icon(
            imageVector = if (expanded) Icons.Outlined.ExpandLess else Icons.Outlined.ExpandMore,
            contentDescription = if (expanded) t.common.collapse else t.common.expand,
            tint = AdminColors.SidebarText.copy(alpha = 0.75f),
            modifier = Modifier.size(18.dp),
        )
    }
}

@Composable
private fun SidebarLink(
    route: AdminRoute,
    current: AdminRoute,
    onSelect: (AdminRoute) -> Unit,
    badgeCount: Long = 0L,
) {
    val selected = current == route
    Row(
        Modifier
            .fillMaxWidth()
            .clickable { onSelect(route) }
            .background(if (selected) AdminColors.SidebarActiveBg else androidx.compose.ui.graphics.Color.Transparent)
            .padding(horizontal = GymlySpace.lg, vertical = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(
            strings().nav.route(route),
            color = if (selected) AdminColors.SidebarActive else AdminColors.SidebarText,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Normal,
            fontSize = 14.sp,
            modifier = Modifier.weight(1f),
        )
        if (badgeCount > 0) {
            Box(
                Modifier
                    .clip(CircleShape)
                    .background(AdminColors.Error)
                    .padding(horizontal = 6.dp, vertical = 2.dp),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    if (badgeCount > 9) "9+" else badgeCount.toString(),
                    color = Color.White,
                    fontSize = 10.sp,
                    fontWeight = FontWeight.Bold,
                )
            }
        }
    }
}

@Composable
fun AdminTopBar(
    userName: String,
    userEmail: String?,
    photoUrl: String? = null,
    photoBytes: ByteArray? = null,
    unreadNotifications: Long,
    notificationItems: List<AdminNotificationItem> = emptyList(),
    compact: Boolean = false,
    showMenuButton: Boolean = false,
    onMenuClick: () -> Unit = {},
    onLogout: () -> Unit,
    onSearchSelect: (AdminSearchHit) -> Unit,
    onRefreshNotifications: () -> Unit = {},
    onUnreadChange: (Long) -> Unit = {},
    onOpenApprovals: () -> Unit = {},
) {
    val t = strings()
    var query by remember { mutableStateOf("") }
    var hits by remember { mutableStateOf<List<AdminSearchHit>>(emptyList()) }
    var bellOpen by remember { mutableStateOf(false) }
    var localItems by remember(notificationItems) { mutableStateOf(notificationItems) }
    var localUnread by remember(unreadNotifications) { mutableStateOf(unreadNotifications) }
    val scope = rememberCoroutineScope()

    fun runSearch(q: String = query) {
        scope.launch {
            hits = try {
                if (q.trim().length >= 2) {
                    AdminRepo.api.search(q).take(12)
                } else {
                    emptyList()
                }
            } catch (_: Exception) {
                emptyList()
            }
        }
    }

    Column(Modifier.fillMaxWidth().background(AdminColors.SurfaceContainer)) {
        Row(
            Modifier
                .fillMaxWidth()
                .padding(horizontal = if (compact) GymlySpace.sm else GymlySpace.lg, vertical = GymlySpace.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(GymlySpace.sm),
        ) {
            if (showMenuButton) {
                IconButton(onClick = onMenuClick) {
                    Icon(Icons.Outlined.Menu, contentDescription = t.shell.menu, tint = AdminColors.Text)
                }
            }
            if (!compact) {
                Box(
                    Modifier
                        .weight(1.4f)
                        .widthIn(min = 180.dp, max = 420.dp)
                        .clip(RoundedCornerShape(12.dp))
                        .background(AdminColors.Surface)
                        .padding(horizontal = 14.dp, vertical = 10.dp),
                ) {
                    if (query.isEmpty()) {
                        Text(t.shell.searchPlaceholder, color = AdminColors.TextMuted, fontSize = 13.sp)
                    }
                    BasicTextField(
                        value = query,
                        onValueChange = { value ->
                            query = value
                            runSearch(value)
                        },
                        singleLine = true,
                        textStyle = TextStyle(color = AdminColors.Text, fontSize = 13.sp),
                        cursorBrush = SolidColor(AdminColors.Primary),
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
            } else {
                Spacer(Modifier.weight(1f))
            }
            Box {
                IconButton(
                    onClick = {
                        bellOpen = true
                        onRefreshNotifications()
                    },
                ) {
                    Box {
                        Icon(
                            Icons.Outlined.Notifications,
                            contentDescription = t.shell.notifications,
                            tint = AdminColors.Text,
                            modifier = Modifier.size(22.dp),
                        )
                        if (localUnread > 0) {
                            Box(
                                Modifier
                                    .align(Alignment.TopEnd)
                                    .size(16.dp)
                                    .clip(CircleShape)
                                    .background(AdminColors.Primary),
                                contentAlignment = Alignment.Center,
                            ) {
                                Text(
                                    if (localUnread > 9) "9+" else localUnread.toString(),
                                    color = AdminColors.OnBrand,
                                    fontSize = 9.sp,
                                    fontWeight = FontWeight.Bold,
                                )
                            }
                        }
                    }
                }
                DropdownMenu(
                    expanded = bellOpen,
                    onDismissRequest = { bellOpen = false },
                    modifier = Modifier.widthIn(min = 300.dp, max = 380.dp),
                ) {
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 12.dp, vertical = 8.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text("Notificaciones", fontWeight = FontWeight.Bold, fontSize = 14.sp)
                        if (localUnread > 0) {
                            TextButton(
                                onClick = {
                                    scope.launch {
                                        runCatching { AdminRepo.api.markAllNotificationsRead() }
                                            .onSuccess { feed ->
                                                localItems = feed.items
                                                localUnread = feed.unreadCount
                                                onUnreadChange(feed.unreadCount)
                                            }
                                    }
                                },
                            ) {
                                Text("Marcar leídas", fontSize = 12.sp, color = AdminColors.Primary)
                            }
                        }
                    }
                    HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.4f))
                    if (localItems.isEmpty()) {
                        Text(
                            "No hay notificaciones",
                            color = AdminColors.TextMuted,
                            fontSize = 13.sp,
                            modifier = Modifier.padding(16.dp),
                        )
                    } else {
                        Column(
                            Modifier
                                .heightIn(max = 360.dp)
                                .verticalScroll(rememberScrollState()),
                        ) {
                            localItems.take(30).forEach { item ->
                                Column(
                                    Modifier
                                        .fillMaxWidth()
                                        .clickable {
                                            scope.launch {
                                                if (!item.read) {
                                                    runCatching { AdminRepo.api.markNotificationRead(item.id) }
                                                        .onSuccess { updated ->
                                                            localItems = localItems.map {
                                                                if (it.id == updated.id) updated else it
                                                            }
                                                            localUnread = (localUnread - 1).coerceAtLeast(0)
                                                            onUnreadChange(localUnread)
                                                        }
                                                }
                                                if (item.isApprovalRelated()) {
                                                    bellOpen = false
                                                    onOpenApprovals()
                                                } else {
                                                    bellOpen = false
                                                }
                                            }
                                        }
                                        .background(
                                            if (item.read) Color.Transparent
                                            else AdminColors.Primary.copy(alpha = 0.06f),
                                        )
                                        .padding(horizontal = 12.dp, vertical = 10.dp),
                                ) {
                                    Text(
                                        item.title,
                                        fontWeight = if (item.read) FontWeight.Medium else FontWeight.SemiBold,
                                        fontSize = 13.sp,
                                        color = AdminColors.Text,
                                    )
                                    item.body?.takeIf { it.isNotBlank() }?.let { body ->
                                        Text(
                                            body,
                                            color = AdminColors.TextMuted,
                                            fontSize = 12.sp,
                                            modifier = Modifier.padding(top = 2.dp),
                                        )
                                    }
                                }
                                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.25f))
                            }
                        }
                    }
                }
            }
            Column(horizontalAlignment = Alignment.End) {
                Text(
                    userName,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 13.sp,
                    color = AdminColors.Text,
                    maxLines = 1,
                )
                if (!compact && !userEmail.isNullOrBlank()) {
                    Text(userEmail, fontSize = 12.sp, color = AdminColors.TextMuted, maxLines = 1)
                }
            }
            val initials = userName.split(" ").mapNotNull { it.firstOrNull()?.uppercaseChar() }.take(2).joinToString("")
            val avatarModel = imagePreviewModel(photoBytes)
            Box(
                Modifier
                    .size(36.dp)
                    .clip(CircleShape)
                    .background(AdminColors.Primary),
                contentAlignment = Alignment.Center,
            ) {
                if (avatarModel != null) {
                    key(avatarModel.takeLast(24)) {
                        AsyncImage(
                            model = avatarModel,
                            contentDescription = null,
                            modifier = Modifier.fillMaxSize(),
                            contentScale = ContentScale.Crop,
                        )
                    }
                } else {
                    Text(initials.ifBlank { "A" }, color = AdminColors.OnBrand, fontWeight = FontWeight.Bold, fontSize = 12.sp)
                }
            }
            Text(
                t.shell.logout,
                color = AdminColors.Primary,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.clickable(onClick = onLogout).padding(GymlySpace.sm),
            )
        }
        if (hits.isNotEmpty() && query.length >= 2) {
            Column(
                Modifier
                    .padding(horizontal = GymlySpace.lg)
                    .padding(bottom = GymlySpace.sm)
                    .clip(RoundedCornerShape(10.dp))
                    .background(AdminColors.Surface)
                    .padding(8.dp),
            ) {
                hits.take(12).forEach { hit ->
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .clickable {
                                onSearchSelect(hit)
                                query = ""
                                hits = emptyList()
                            }
                            .padding(8.dp),
                    ) {
                        Text(hit.title, fontWeight = FontWeight.SemiBold, fontSize = 13.sp, color = AdminColors.Text)
                        Text(
                            listOfNotNull(t.shell.searchType(hit.type), hit.subtitle).joinToString(" · "),
                            color = AdminColors.TextMuted,
                            fontSize = 12.sp,
                        )
                    }
                }
            }
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
    }
}

